import { useState, useEffect, Fragment } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { FileBarChart, Printer, Search, FileText, FileDown } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';
import FacturasEmitidasModal from './FacturasEmitidasModal';
import DetalleItemsOTModal from './DetalleItemsOTModal';
import FacturasEmitidasSection from './FacturasEmitidasSection';

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0';
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(value);
};

export default function InformeCostoOT() {
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromOT, setFromOT] = useState('');
  const [toOT, setToOT] = useState('');
  const [fromSearch, setFromSearch] = useState('');
  const [toSearch, setToSearch] = useState('');
  // Iter 333 (Renzo): por defecto el informe considera las OT EN EJECUCION
  const [filtroEstado, setFiltroEstado] = useState('EN EJECUCION');
  const [reportData, setReportData] = useState(null);
  // Iter 293.o (Carlos): modal detalle facturas emitidas por OT
  const [selectedFacturasOT, setSelectedFacturasOT] = useState(null);
  // Iter 294.a (Carlos): modal drill-down materiales/servicios por OT
  const [itemsModal, setItemsModal] = useState(null);
  // Iter 295: exportación PDF consolidada
  const [exportingPdf, setExportingPdf] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const otsRes = await api.get('/ordenes-trabajo');
      const sorted = otsRes.data.sort((a, b) => a.numero_ot - b.numero_ot);
      setOrdenes(sorted);
      // Iter 333: preseleccionar el rango de las OT EN EJECUCION por defecto
      const enEjec = sorted.filter(ot => ot.estado === 'EN EJECUCION');
      if (enEjec.length > 0) {
        setFromOT(enEjec[0].numero_ot.toString());
        setToOT(enEjec[enEjec.length - 1].numero_ot.toString());
      }
    } catch (_error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Iter 333: cambiar el estado re-preselecciona el rango del nuevo estado
  const handleEstadoChange = (estado) => {
    setFiltroEstado(estado);
    const lista = estado ? ordenes.filter(ot => ot.estado === estado) : ordenes;
    if (lista.length > 0) {
      setFromOT(lista[0].numero_ot.toString());
      setToOT(lista[lista.length - 1].numero_ot.toString());
    } else {
      setFromOT(''); setToOT('');
    }
  };

  // Filter OTs based on search - mantener orden correlativo
  const filteredFromOTs = ordenes
    .filter(ot => {
      if (filtroEstado && ot.estado !== filtroEstado) return false;
      if (!fromSearch) return true;
      const search = fromSearch.toLowerCase();
      return (
        ot.numero_ot.toString().includes(search) ||
        ot.descripcion?.toLowerCase().includes(search) ||
        ot.proyecto_codigo?.toLowerCase().includes(search)
      );
    })
    .sort((a, b) => a.numero_ot - b.numero_ot);

  const filteredToOTs = ordenes
    .filter(ot => {
      if (filtroEstado && ot.estado !== filtroEstado) return false;
      if (!toSearch) return true;
      const search = toSearch.toLowerCase();
      return (
        ot.numero_ot.toString().includes(search) ||
        ot.descripcion?.toLowerCase().includes(search) ||
        ot.proyecto_codigo?.toLowerCase().includes(search)
      );
    })
    .sort((a, b) => a.numero_ot - b.numero_ot);

  const generateReport = async () => {
    if (!fromOT || !toOT) {
      toast.error('Seleccione el rango de OTs (Desde y Hasta)');
      return;
    }

    const fromNum = parseInt(fromOT);
    const toNum = parseInt(toOT);

    if (fromNum > toNum) {
      toast.error('La OT "Desde" debe ser menor o igual a la OT "Hasta"');
      return;
    }

    const selectedData = ordenes.filter(ot =>
      ot.numero_ot >= fromNum && ot.numero_ot <= toNum
      && (!filtroEstado || ot.estado === filtroEstado)
    );

    if (selectedData.length === 0) {
      toast.error('No hay OTs en el rango seleccionado');
      return;
    }

    setLoading(true);
    
    try {
      // Fetch real costs from the backend for each OT
      const costosRealesPromises = selectedData.map(async (ot) => {
        try {
          const res = await api.get(`/compras/costo-materiales-por-ot?ot_numero=${ot.numero_ot}`);
          return {
            numero_ot: ot.numero_ot,
            // Iter 293.n: separación por cuenta_clasif (MA/SE/GA)
            materiales: res.data.total_materiales || 0,
            sstt: res.data.total_sstt || 0,
            gastos_generales: res.data.total_gastos_generales || 0,
            total_costo_neto: res.data.total_costo_neto || 0,
            cantOC: res.data.cantidad_ocs || 0
          };
        } catch {
          return { numero_ot: ot.numero_ot, materiales: 0, sstt: 0, gastos_generales: 0, total_costo_neto: 0, cantOC: 0 };
        }
      });
      
      const costosRealesArray = await Promise.all(costosRealesPromises);
      const costosRealesMap = {};
      costosRealesArray.forEach(c => { costosRealesMap[c.numero_ot] = c; });

      // Iter 293.l (Carlos, feb-2026): HH reales cargadas desde `control_hh`.
      const hhPromises = selectedData.map(async (ot) => {
        try {
          const res = await api.get(`/control-hh/resumen-ot/${ot.numero_ot}`);
          return {
            numero_ot: ot.numero_ot,
            total_horas: Number(res.data.total_horas) || 0,
            horas_normales: Number(res.data.total_horas_normales) || 0,
            horas_extra: Number(res.data.total_horas_extra) || 0,
          };
        } catch {
          return { numero_ot: ot.numero_ot, total_horas: 0, horas_normales: 0, horas_extra: 0 };
        }
      });
      const hhArray = await Promise.all(hhPromises);
      const hhMap = {};
      hhArray.forEach(h => { hhMap[h.numero_ot] = h; });

      // Iter 293.m (Carlos, feb-2026): traer FACTURADO real (neto) por OT.
      // Antes se usaba `ot.monto` que es el PRESUPUESTO total de la OT,
      // no lo facturado. Ahora se consulta `/api/ventas/por-ot/{n}`.
      // Iter 293.o (Carlos): guardar también el listado de facturas para el
      // modal "Ver detalle de facturas emitidas".
      const facturadoPromises = selectedData.map(async (ot) => {
        try {
          const res = await api.get(`/ventas/por-ot/${ot.numero_ot}`);
          return {
            numero_ot: ot.numero_ot,
            neto: Number(res.data.total_facturado_neto) || 0,
            cantidad: Number(res.data.cantidad_facturas) || 0,
            ventas: res.data.ventas || [],
          };
        } catch {
          return { numero_ot: ot.numero_ot, neto: 0, cantidad: 0, ventas: [] };
        }
      });
      const facturadoArray = await Promise.all(facturadoPromises);
      const facturadoMap = {};
      facturadoArray.forEach(f => { facturadoMap[f.numero_ot] = f; });

      // Calculate report data with REAL (from OC) and PRES (from presupuesto)
      const otData = selectedData.map(ot => {
        const pres = ot.presupuesto || {};
        const costosReales = costosRealesMap[ot.numero_ot] || { materiales: 0, cantOC: 0 };
        
        // PRESUPUESTO values
        const presMateriales = pres.materiales || 0;
        const presSSTT = pres.servicios_terceros || 0;
        const presCantHH = pres.numero_hh || 0;
        const presValorHH = pres.valor_hh || 12500;
        const presManoObra = presCantHH * presValorHH;
        const presGastosGrales = pres.gastos_generales || 0;
        const presTotal = presMateriales + presSSTT + presManoObra + presGastosGrales;
        const presFacturado = pres.total || 0;
        const presUtilidad = presFacturado - presTotal;
        
        // REAL values - desde detalle de OC + HH reales cargadas
        const realMateriales = costosReales.materiales;
        // Iter 293.n: SSTT y Gastos Grales ahora vienen del backend (según cuenta_clasif)
        const realSSTT = costosReales.sstt || 0;
        const hhInfo = hhMap[ot.numero_ot] || { total_horas: 0 };
        const realCantHH = hhInfo.total_horas;
        const realValorHH = pres.valor_hh || 12500;
        const realManoObra = realCantHH * realValorHH;
        const realGastosGrales = costosReales.gastos_generales || 0;
        // Iter 293.m: Total Gastos REAL = materiales OC + SSTT + Mano Obra + G. Grales
        const realTotalGastos = realMateriales + realSSTT + realManoObra + realGastosGrales;
        // Iter 293.m: Facturado REAL = neto facturado por esta OT (colección `ventas`).
        const realFacturado = (facturadoMap[ot.numero_ot] || {}).neto || 0;
        // Utilidad REAL = Facturado - Total Gastos
        const realUtilidad = realFacturado - realTotalGastos;
        
        return {
          ...ot,
          real: {
            materiales: realMateriales,
            sstt: realSSTT,
            cantHH: realCantHH,
            valorHH: realValorHH,
            manoObra: realManoObra,
            gastosGrales: realGastosGrales,
            totalGastos: realTotalGastos,
            total: realTotalGastos,  // backward compat
            facturado: realFacturado,
            cantFacturas: (facturadoMap[ot.numero_ot] || {}).cantidad || 0,
            ventasList: (facturadoMap[ot.numero_ot] || {}).ventas || [],
            utilidad: realUtilidad,
            cantOC: costosReales.cantOC
          },
          pres: {
            materiales: presMateriales,
            sstt: presSSTT,
            cantHH: presCantHH,
            valorHH: presValorHH,
            manoObra: presManoObra,
            gastosGrales: presGastosGrales,
            totalGastos: presTotal,
            total: presTotal,
            facturado: presFacturado,
            utilidad: presUtilidad
          },
          esAumentoObra: ot.es_aumento_obra || false
        };
      });

      // Calculate totals
      const totalesReal = {
        materiales: otData.reduce((sum, ot) => sum + ot.real.materiales, 0),
        sstt: otData.reduce((sum, ot) => sum + ot.real.sstt, 0),
        cantHH: Math.round(otData.reduce((sum, ot) => sum + ot.real.cantHH, 0) * 100) / 100,
        manoObra: otData.reduce((sum, ot) => sum + ot.real.manoObra, 0),
        gastosGrales: otData.reduce((sum, ot) => sum + ot.real.gastosGrales, 0),
        totalGastos: otData.reduce((sum, ot) => sum + ot.real.totalGastos, 0),
        total: otData.reduce((sum, ot) => sum + ot.real.total, 0),
        facturado: otData.reduce((sum, ot) => sum + ot.real.facturado, 0),
        utilidad: otData.reduce((sum, ot) => sum + ot.real.utilidad, 0),
      };
      
      const totalesPres = {
        materiales: otData.reduce((sum, ot) => sum + ot.pres.materiales, 0),
        sstt: otData.reduce((sum, ot) => sum + ot.pres.sstt, 0),
        cantHH: Math.round(otData.reduce((sum, ot) => sum + ot.pres.cantHH, 0) * 100) / 100,
        manoObra: otData.reduce((sum, ot) => sum + ot.pres.manoObra, 0),
        gastosGrales: otData.reduce((sum, ot) => sum + ot.pres.gastosGrales, 0),
        totalGastos: otData.reduce((sum, ot) => sum + ot.pres.totalGastos, 0),
        total: otData.reduce((sum, ot) => sum + ot.pres.total, 0),
        facturado: otData.reduce((sum, ot) => sum + ot.pres.facturado, 0),
        utilidad: otData.reduce((sum, ot) => sum + ot.pres.utilidad, 0),
      };

      setReportData({ ots: otData, totalesReal, totalesPres, fromOT: fromNum, toOT: toNum });
      toast.success(`Informe generado con ${otData.length} OTs`);
    } catch (_error) {

      toast.error('Error al generar el informe');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => window.print();

  // Iter 295: PDF consolidado de las OTs del informe
  const handleExportPDF = async () => {
    if (!reportData) return;
    setExportingPdf(true);
    try {
      const otsParam = reportData.ots.map(o => o.numero_ot).join(',');
      const res = await api.get(`/informes/ots-pdf?ots=${otsParam}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Informe_OTs_${reportData.fromOT}_a_${reportData.toOT}_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Informe PDF descargado');
    } catch {
      toast.error('Error al generar el PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  if (loading && ordenes.length === 0) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-4" data-testid="informe-costo-ot-page">
      {/* Header */}
      <div className="flex items-center justify-between print:hidden">
        <div className="flex items-center gap-3">
          <FileBarChart className="h-8 w-8 text-purple-600" />
          <div>
            <h1 className="text-2xl font-bold">Informe de Costo por OT</h1>
            <p className="text-muted-foreground text-sm">Seleccione rango de OTs (Desde - Hasta)</p>
          </div>
        </div>
      </div>

      {/* Selection Controls */}
      <Card className="print:hidden">
        <CardContent className="p-4">
          <div className="mb-3 flex items-center gap-2">
            <label className="text-sm font-medium">Estado:</label>
            <select
              value={filtroEstado}
              onChange={(e) => handleEstadoChange(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              data-testid="select-estado-informe-costo"
            >
              <option value="EN EJECUCION">EN EJECUCION</option>
              <option value="PENDIENTE">PENDIENTE</option>
              <option value="COMPLETADA">COMPLETADA</option>
              <option value="">Todos</option>
            </select>
            {filtroEstado && (
              <span className="text-xs text-muted-foreground">
                El informe incluye sólo OTs {filtroEstado} dentro del rango.
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Desde OT */}
            <div>
              <label className="text-sm font-medium mb-2 block">Desde OT</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por N° OT o descripción..."
                  value={fromSearch}
                  onChange={(e) => {
                    setFromSearch(e.target.value);
                    // Auto-select first match if searching
                    const filtered = ordenes.filter(ot => {
                      if (filtroEstado && ot.estado !== filtroEstado) return false;
                      const search = e.target.value.toLowerCase();
                      return ot.numero_ot.toString().includes(search) ||
                        ot.descripcion?.toLowerCase().includes(search);
                    });
                    if (filtered.length > 0 && e.target.value) {
                      setFromOT(filtered[0].numero_ot.toString());
                    }
                  }}
                  className="pl-10 mb-2"
                />
              </div>
              <select 
                value={fromOT} 
                onChange={(e) => setFromOT(e.target.value)}
                className="h-32 w-full rounded-md border border-input bg-background px-3 text-sm"
                size={6}
              >
                {filteredFromOTs.map(ot => (
                  <option key={ot.id} value={ot.numero_ot}>
                    OT-{ot.numero_ot} | {ot.descripcion?.substring(0, 50)}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                {fromOT ? <span className="text-green-600 font-medium">OT-{fromOT}</span> : 'Ninguna'} | {filteredFromOTs.length} OTs
              </p>
            </div>

            {/* Hasta OT */}
            <div>
              <label className="text-sm font-medium mb-2 block">Hasta OT</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por N° OT o descripción..."
                  value={toSearch}
                  onChange={(e) => {
                    setToSearch(e.target.value);
                    // Auto-select first match if searching
                    const filtered = ordenes.filter(ot => {
                      if (filtroEstado && ot.estado !== filtroEstado) return false;
                      const search = e.target.value.toLowerCase();
                      return ot.numero_ot.toString().includes(search) ||
                        ot.descripcion?.toLowerCase().includes(search);
                    });
                    if (filtered.length > 0 && e.target.value) {
                      setToOT(filtered[0].numero_ot.toString());
                    }
                  }}
                  className="pl-10 mb-2"
                />
              </div>
              <select 
                value={toOT} 
                onChange={(e) => setToOT(e.target.value)}
                className="h-32 w-full rounded-md border border-input bg-background px-3 text-sm"
                size={6}
              >
                {filteredToOTs.map(ot => (
                  <option key={ot.id} value={ot.numero_ot}>
                    OT-{ot.numero_ot} | {ot.descripcion?.substring(0, 50)}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                {toOT ? <span className="text-green-600 font-medium">OT-{toOT}</span> : 'Ninguna'} | {filteredToOTs.length} OTs
              </p>
            </div>
          </div>

          <div className="flex gap-2 mt-4 justify-end">
            <Button onClick={generateReport} disabled={!fromOT || !toOT || loading}>
              {loading ? 'Generando...' : 'Generar Informe'}
            </Button>
            {reportData && (
              <>
                <Button
                  onClick={handleExportPDF}
                  disabled={exportingPdf}
                  className="bg-red-700 hover:bg-red-800"
                  data-testid="btn-exportar-pdf-ots"
                >
                  <FileDown className="h-4 w-4 mr-2" />
                  {exportingPdf ? 'Generando...' : 'Exportar PDF'}
                </Button>
                <Button variant="outline" onClick={handlePrint}>
                  <Printer className="h-4 w-4 mr-2" />Imprimir
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Report */}
      {reportData && (
        <>
          {/* Print Header */}
          <div className="hidden print:block mb-4">
            <div className="text-center border-b-2 border-black pb-2">
              <h1 className="text-xl font-bold">CHIRINO STEEL</h1>
              <h2 className="text-lg">Informe de Costo por OT</h2>
              <p className="text-sm">Rango: OT-{reportData.fromOT} a OT-{reportData.toOT} | {reportData.ots.length} OTs</p>
              <p className="text-sm text-gray-600">Fecha: {new Date().toLocaleDateString('es-CL')}</p>
            </div>
          </div>

          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-lg">
                Detalle de Costos - OT-{reportData.fromOT} a OT-{reportData.toOT} ({reportData.ots.length} OTs)
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                REAL: Costos desde OC aprobadas y facturación | PRES: Valores presupuestados
              </p>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="text-xs bg-muted/50">
                      <TableHead className="py-2 min-w-[180px]">OT / Proyecto / Descripción</TableHead>
                      <TableHead className="py-2 w-14">Tipo</TableHead>
                      <TableHead className="py-2 text-right">Materiales</TableHead>
                      <TableHead className="py-2 text-right">SSTT</TableHead>
                      <TableHead className="py-2 text-right">Cant. HH</TableHead>
                      <TableHead className="py-2 text-right">Mano Obra</TableHead>
                      <TableHead className="py-2 text-right">Gtos. Grales.</TableHead>
                      <TableHead className="py-2 text-right bg-slate-200 dark:bg-slate-800">Total Gastos</TableHead>
                      <TableHead className="py-2 text-right">Facturado</TableHead>
                      <TableHead className="py-2 text-right">Utilidad</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.ots.map((ot, idx) => {
                      const prev = reportData.ots[idx - 1];
                      const cambiaProyecto = !prev || prev.proyecto_codigo !== ot.proyecto_codigo;
                      return (
                      <Fragment key={ot.id}>
                        {/* Iter 334: línea llamativa al cambiar de proyecto */}
                        {cambiaProyecto && (
                          <TableRow className="bg-indigo-700 hover:bg-indigo-700 border-t-4 border-indigo-950" data-testid={`separador-proyecto-${ot.numero_ot}`}>
                            <TableCell colSpan={10} className="py-1.5 text-white font-bold text-xs uppercase tracking-widest">
                              ▸ Proyecto {ot.proyecto_codigo || 'sin código'}{ot.cliente_nombre ? ` — ${ot.cliente_nombre}` : ''}
                            </TableCell>
                          </TableRow>
                        )}
                        {/* Línea REAL */}
                        <TableRow key={`${ot.id}-real`} className="text-sm bg-blue-50/50">
                          <TableCell className="py-1 font-mono" rowSpan={2}>
                            <span className="font-bold">OT-{ot.numero_ot}</span>
                            {ot.esAumentoObra && <span className="text-orange-600 text-xs ml-1">(AO)</span>}
                            <span className="block text-xs text-gray-500">{ot.proyecto_codigo}</span>
                            <span className="block text-xs text-gray-600 line-clamp-2">{ot.descripcion}</span>
                          </TableCell>
                          <TableCell className="py-1 font-semibold text-blue-700 text-xs">REAL</TableCell>
                          <TableCell className="py-1 text-right font-mono text-xs">
                            <button
                              type="button"
                              onClick={() => setItemsModal({ ot, tipo: 'MA' })}
                              className="text-blue-700 hover:text-blue-900 hover:underline"
                              title="Ver detalle de materiales"
                              data-testid={`btn-items-ma-${ot.numero_ot}`}
                            >
                              {formatCurrency(ot.real.materiales)}
                            </button>
                            {ot.real.cantOC > 0 && <span className="block text-blue-600 text-[10px]">({ot.real.cantOC} OC)</span>}
                          </TableCell>
                          <TableCell className="py-1 text-right font-mono text-xs">
                            <button
                              type="button"
                              onClick={() => setItemsModal({ ot, tipo: 'SE' })}
                              className="text-amber-700 hover:text-amber-900 hover:underline"
                              title="Ver detalle de servicios (SSTT)"
                              data-testid={`btn-items-se-${ot.numero_ot}`}
                            >
                              {formatCurrency(ot.real.sstt)}
                            </button>
                          </TableCell>
                          <TableCell className="py-1 text-right font-mono text-xs">{ot.real.cantHH}</TableCell>
                          <TableCell className="py-1 text-right font-mono text-xs">{formatCurrency(ot.real.manoObra)}</TableCell>
                          <TableCell className="py-1 text-right font-mono text-xs">
                            <button
                              type="button"
                              onClick={() => setItemsModal({ ot, tipo: 'GA' })}
                              className="text-purple-700 hover:text-purple-900 hover:underline"
                              title="Ver detalle de gastos generales"
                              data-testid={`btn-items-ga-${ot.numero_ot}`}
                            >
                              {formatCurrency(ot.real.gastosGrales)}
                            </button>
                          </TableCell>
                          <TableCell className="py-1 text-right font-mono text-xs font-semibold bg-slate-100 dark:bg-slate-800">{formatCurrency(ot.real.totalGastos)}</TableCell>
                          <TableCell className="py-1 text-right font-mono text-xs">
                            {ot.real.cantFacturas > 0 ? (
                              <button
                                type="button"
                                onClick={() => setSelectedFacturasOT(ot)}
                                className="text-blue-700 hover:text-blue-900 hover:underline inline-flex items-center gap-1"
                                title="Ver facturas emitidas"
                                data-testid={`btn-ver-facturas-${ot.numero_ot}`}
                              >
                                {formatCurrency(ot.real.facturado)}
                                <FileText className="w-3 h-3" />
                              </button>
                            ) : (
                              formatCurrency(ot.real.facturado)
                            )}
                            {ot.real.cantFacturas > 0 && <span className="block text-blue-600 text-[10px]">({ot.real.cantFacturas} fact)</span>}
                          </TableCell>
                          <TableCell className={`py-1 text-right font-mono text-xs font-semibold ${ot.real.utilidad >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(ot.real.utilidad)}
                          </TableCell>
                        </TableRow>
                        {/* Línea PRESUPUESTO */}
                        <TableRow key={`${ot.id}-pres`} className="text-sm bg-gray-50 border-b-2">
                          <TableCell className="py-1 font-semibold text-gray-500 text-xs">PRES</TableCell>
                          <TableCell className="py-1 text-right font-mono text-xs text-gray-600">{formatCurrency(ot.pres.materiales)}</TableCell>
                          <TableCell className="py-1 text-right font-mono text-xs text-gray-600">{formatCurrency(ot.pres.sstt)}</TableCell>
                          <TableCell className="py-1 text-right font-mono text-xs text-gray-600">{ot.pres.cantHH}</TableCell>
                          <TableCell className="py-1 text-right font-mono text-xs text-gray-600">{formatCurrency(ot.pres.manoObra)}</TableCell>
                          <TableCell className="py-1 text-right font-mono text-xs text-gray-600">{formatCurrency(ot.pres.gastosGrales)}</TableCell>
                          <TableCell className="py-1 text-right font-mono text-xs text-gray-600 font-semibold bg-slate-100/60 dark:bg-slate-800/60">{formatCurrency(ot.pres.totalGastos)}</TableCell>
                          <TableCell className="py-1 text-right font-mono text-xs text-gray-600">{formatCurrency(ot.pres.facturado)}</TableCell>
                          <TableCell className={`py-1 text-right font-mono text-xs ${ot.pres.utilidad >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {formatCurrency(ot.pres.utilidad)}
                          </TableCell>
                        </TableRow>
                      </Fragment>
                      );
                    })}
                    {/* Total REAL */}
                    <TableRow className="bg-blue-800 text-white font-bold">
                      <TableCell className="py-2">TOTAL</TableCell>
                      <TableCell className="py-2 text-xs">REAL</TableCell>
                      <TableCell className="py-2 text-right font-mono text-xs">{formatCurrency(reportData.totalesReal.materiales)}</TableCell>
                      <TableCell className="py-2 text-right font-mono text-xs">{formatCurrency(reportData.totalesReal.sstt)}</TableCell>
                      <TableCell className="py-2 text-right font-mono text-xs">{reportData.totalesReal.cantHH}</TableCell>
                      <TableCell className="py-2 text-right font-mono text-xs">{formatCurrency(reportData.totalesReal.manoObra)}</TableCell>
                      <TableCell className="py-2 text-right font-mono text-xs">{formatCurrency(reportData.totalesReal.gastosGrales)}</TableCell>
                      <TableCell className="py-2 text-right font-mono text-xs bg-blue-900">{formatCurrency(reportData.totalesReal.totalGastos)}</TableCell>
                      <TableCell className="py-2 text-right font-mono text-xs">{formatCurrency(reportData.totalesReal.facturado)}</TableCell>
                      <TableCell className={`py-2 text-right font-mono text-xs ${reportData.totalesReal.utilidad >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                        {formatCurrency(reportData.totalesReal.utilidad)}
                      </TableCell>
                    </TableRow>
                    {/* Total PRESUPUESTO */}
                    <TableRow className="bg-gray-600 text-white font-bold">
                      <TableCell className="py-2"></TableCell>
                      <TableCell className="py-2 text-xs">PRES</TableCell>
                      <TableCell className="py-2 text-right font-mono text-xs">{formatCurrency(reportData.totalesPres.materiales)}</TableCell>
                      <TableCell className="py-2 text-right font-mono text-xs">{formatCurrency(reportData.totalesPres.sstt)}</TableCell>
                      <TableCell className="py-2 text-right font-mono text-xs">{reportData.totalesPres.cantHH}</TableCell>
                      <TableCell className="py-2 text-right font-mono text-xs">{formatCurrency(reportData.totalesPres.manoObra)}</TableCell>
                      <TableCell className="py-2 text-right font-mono text-xs">{formatCurrency(reportData.totalesPres.gastosGrales)}</TableCell>
                      <TableCell className="py-2 text-right font-mono text-xs bg-gray-700">{formatCurrency(reportData.totalesPres.totalGastos)}</TableCell>
                      <TableCell className="py-2 text-right font-mono text-xs">{formatCurrency(reportData.totalesPres.facturado)}</TableCell>
                      <TableCell className={`py-2 text-right font-mono text-xs ${reportData.totalesPres.utilidad >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                        {formatCurrency(reportData.totalesPres.utilidad)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 print:hidden">
            <Card className="border-blue-200">
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">Utilidad REAL</p>
                <p className={`text-xl font-bold ${reportData.totalesReal.utilidad >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(reportData.totalesReal.utilidad)}
                </p>
                <p className="text-xs text-muted-foreground">Facturado - Costos OC</p>
              </CardContent>
            </Card>
            <Card className="border-gray-200">
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">Utilidad PRES</p>
                <p className={`text-xl font-bold ${reportData.totalesPres.utilidad >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(reportData.totalesPres.utilidad)}
                </p>
                <p className="text-xs text-muted-foreground">Presupuesto original</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">Diferencia (Real-Pres)</p>
                <p className={`text-xl font-bold ${(reportData.totalesReal.utilidad - reportData.totalesPres.utilidad) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(reportData.totalesReal.utilidad - reportData.totalesPres.utilidad)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">Margen Real</p>
                <p className={`text-xl font-bold ${reportData.totalesReal.utilidad >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {reportData.totalesReal.facturado > 0 
                    ? `${((reportData.totalesReal.utilidad / reportData.totalesReal.facturado) * 100).toFixed(1)}%`
                    : '0%'}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Iter 295: Facturas emitidas de todas las OTs del informe */}
          <FacturasEmitidasSection ots={reportData.ots} />
        </>
      )}

      {/* Iter 293.o: Modal Detalle Facturas Emitidas (compartido) */}
      <FacturasEmitidasModal ot={selectedFacturasOT} onClose={() => setSelectedFacturasOT(null)} />

      {/* Iter 294.a: Modal Detalle Materiales/Servicios por OT */}
      <DetalleItemsOTModal
        ot={itemsModal?.ot || null}
        tipoInicial={itemsModal?.tipo}
        onClose={() => setItemsModal(null)}
      />
    </div>
  );
}
